import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Room } from '../../rooms.js';
import { useSheetDismiss } from '../../gestures/SwipeProvider.js';
import { RoomIcon } from './RoomIcon.js';

/**
 * Das Raum-Blatt (27.10.2026): die Räume groß zum Antippen, wie die übrigen
 * Blätter als Glas von unten. Escape und „Schließen“ schließen, nach unten
 * wischen auch (`useSheetDismiss`); der Fokus springt auf den aktuellen Raum
 * und geht beim Schließen an den Raum-Knopf zurück (macht die Leiste).
 */
export function RoomSheet({
  rooms,
  current,
  onPick,
  onClose,
}: {
  rooms: readonly Room[];
  current: Room;
  onPick: (room: Room) => void;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const sheet = useSheetDismiss<HTMLDivElement>(onClose, false);
  const currentRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    currentRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hints: Record<Room, string> = {
    me: t('rooms.meHint'),
    household: t('rooms.householdHint'),
    family: t('rooms.familyHint'),
    community: t('rooms.communityHint'),
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="room-sheet-title"
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/30 p-3"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={sheet}
        className="kw-glass flex max-h-[85dvh] w-full max-w-lg flex-col gap-3 overflow-y-auto rounded-[1.5rem] p-4"
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 id="room-sheet-title" className="font-display text-xl font-bold">
            {t('rooms.sheetTitle')}
          </h2>
          <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet kw-btn-small px-3">
            {t('rooms.close')}
          </button>
        </div>
        <ul className="flex flex-col gap-2">
          {rooms.map((room) => {
            const active = room === current;
            return (
              <li key={room}>
                <button
                  ref={active ? currentRef : undefined}
                  type="button"
                  aria-current={active ? 'true' : undefined}
                  aria-labelledby={`room-name-${room}`}
                  aria-describedby={`room-hint-${room}`}
                  onClick={() => onPick(room)}
                  className={`flex min-h-16 w-full items-center gap-4 rounded-2xl border px-4 py-3 text-left ${
                    active ? 'border-accent' : 'border-line'
                  }`}
                >
                  <span
                    className={`flex size-11 shrink-0 items-center justify-center rounded-full ${
                      active ? 'bg-accent text-ground' : 'bg-surface text-ink-muted'
                    }`}
                  >
                    <RoomIcon room={room} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span id={`room-name-${room}`} className="block text-lg font-semibold">
                      {t(roomLabelKey[room])}
                    </span>
                    <span id={`room-hint-${room}`} className="block text-sm text-ink-muted">
                      {active ? `${t('rooms.current')} · ` : ''}
                      {hints[room]}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <p className="text-xs text-ink-muted">{t('rooms.sheetHint')}</p>
      </div>
    </div>
  );
}

/** Schlüssel des Raumnamens — als Wörter, damit die Abdeckung sie findet. */
export const roomLabelKey = {
  me: 'rooms.me',
  household: 'rooms.household',
  family: 'rooms.family',
  community: 'rooms.community',
} as const satisfies Record<Room, string>;
